import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import SalesMetricsPanel from './components/SalesMetricsPanel';
import SalesFilterToolbar from './components/SalesFilterToolbar';
import SalesDataTable from './components/SalesDataTable';
import TabNavigation from './components/TabNavigation';
import AIInsightsPanel from './components/AIInsightsPanel';
import Button from '../../components/ui/Button';
import SalesFormModal from './components/SalesFormModal';
import SalesActionDropdown from './components/SalesActionDropdown';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import ViewDetailsModal from './components/ViewDetailsModal';
import RecordPaymentModal from './components/RecordPaymentModal';
import DuplicateCustomerModal from './components/DuplicateCustomerModal';
import { useAuth } from '../../contexts/AuthContext';
import { useDataRefresh, useDataLoader } from '../../hooks/useDataRefresh';
import {
  customerService,
  invoiceService,
  quoteService,
  paymentService,
  deliveryService,
  retainerService,
  recurringService,
  creditNoteService,
  salesMetricsService,
  salesSubscriptions
} from '../../services/salesHubService';

const SalesHub = () => {
  const navigate = useNavigate();
  const { user, userProfile, isAuthenticated } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('customers');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [filters, setFilters] = useState({
    customer: '',
    dateFrom: '',
    dateTo: '',
    status: '',
    minAmount: '',
    maxAmount: ''
  });

  // Enhanced data refresh management
  const { refreshTrigger, isRefreshing, triggerRefresh, forceRefresh, cleanup } = useDataRefresh();

  // Data states with enhanced loading management
  const [salesData, setSalesData] = useState([]);
  const [salesMetrics, setSalesMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add separate customer data state for dropdown options
  const [customerData, setCustomerData] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Add new state for modals
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);

  // Add new state for record payment modal
  const [isRecordPaymentModalOpen, setIsRecordPaymentModalOpen] = useState(false);
  const [paymentInvoice, setPaymentInvoice] = useState(null);
  const [recordPaymentLoading, setRecordPaymentLoading] = useState(false);

  // Add state for duplicate customer modal
  const [isDuplicateCustomerModalOpen, setIsDuplicateCustomerModalOpen] = useState(false);
  const [duplicatingCustomer, setDuplicatingCustomer] = useState(null);

  // Get organization ID
  const organizationId = userProfile?.organization_id;

  // Load customer data separately for dropdown options
  const loadCustomerOptions = async () => {
    if (!organizationId || !isAuthenticated) {
      setCustomerData([]);
      return;
    }

    try {
      setCustomersLoading(true);
      let result = await customerService?.getCustomers(organizationId, { status: 'active' });

      if (result?.error) {
        console.error('Error loading customer options:', result?.error);
        setCustomerData([]);
      } else {
        setCustomerData(result?.data || []);
      }
    } catch (error) {
      console.error('Error loading customer options:', error);
      setCustomerData([]);
    } finally {
      setCustomersLoading(false);
    }
  };

  // Load customer options on mount and when organization changes
  useEffect(() => {
    loadCustomerOptions();
  }, [organizationId, isAuthenticated]);

  // Reload customer options when customers are created/updated
  useEffect(() => {
    if (refreshTrigger) {
      loadCustomerOptions();
    }
  }, [refreshTrigger]);

  // Enhanced data loading function with retry logic
  const createLoadFunction = (tab, currentFilters) => {
    return async () => {
      if (!organizationId || !isAuthenticated) {
        return { data: [], error: null };
      }

      let result = { data: [], error: null };

      switch (tab) {
        case 'customers':
          result = await customerService?.getCustomers(organizationId, currentFilters);
          break;
        case 'quotes':
          result = await quoteService?.getQuotes(organizationId, currentFilters);
          break;
        case 'retainer-invoices':
          result = await retainerService?.getRetainerInvoices(organizationId, currentFilters);
          break;
        case 'invoices':
          result = await invoiceService?.getInvoices(organizationId, currentFilters);
          break;
        case 'recurring-invoices':
          result = await recurringService?.getRecurringInvoices(organizationId, currentFilters);
          break;
        case 'delivery-challans':
          result = await deliveryService?.getDeliveryChallans(organizationId, currentFilters);
          break;
        case 'payment-links':
          result = await paymentService?.getPaymentLinks(organizationId, currentFilters);
          break;
        case 'payments-received':
          result = await paymentService?.getPaymentsReceived(organizationId, currentFilters);
          break;
        case 'credit-notes':
          result = await creditNoteService?.getCreditNotes(organizationId, currentFilters);
          break;
        default:
          break;
      }

      return result;
    };
  };

  // Use enhanced data loader with retry logic
  const loadFunction = useMemo(() =>
    createLoadFunction(activeTab, filters),
    [activeTab, organizationId, isAuthenticated]
  );

  const {
    data: loadedData,
    loading: dataLoading,
    error: dataError,
    reload: reloadData
  } = useDataLoader(loadFunction, [refreshTrigger, activeTab], {
    retryAttempts: 3,
    retryDelay: 1000,
    optimisticUpdate: true
  });

  // Update sales data when loaded data changes
  useEffect(() => {
    setSalesData(loadedData || []);
    setLoading(dataLoading);
    setError(dataError);
  }, [loadedData, dataLoading, dataError]);

  // Reload data when filters change (with debouncing)
  useEffect(() => {
    const timeout = setTimeout(() => {
      reloadData();
    }, 300); // Debounce filter changes

    return () => clearTimeout(timeout);
  }, [filters, reloadData]);

  // Load sales metrics with enhanced error handling
  const loadMetrics = async () => {
    if (!organizationId || !isAuthenticated) return;

    try {
      let result = await salesMetricsService?.getSalesMetrics(organizationId);

      if (result?.error) {
        console.error('Error loading metrics:', result?.error);
        return;
      }

      const data = result?.data;
      const metrics = [
        {
          label: 'Total Outstanding',
          value: `$${data?.totalOutstanding?.toLocaleString() || '0'}`,
          icon: 'DollarSign',
          bgColor: 'bg-primary',
          trend: 'up',
          change: data?.totalOutstanding > 0 ? '' : 'No data'
        },
        {
          label: 'Overdue Amount',
          value: `$${data?.overdueAmount?.toLocaleString() || '0'}`,
          icon: 'AlertTriangle',
          bgColor: 'bg-error',
          trend: 'down',
          change: data?.overdueAmount > 0 ? '' : 'No data'
        },
        {
          label: 'Quote Conversion',
          value: `${data?.conversionRate?.toFixed(1) || '0'}%`,
          icon: 'TrendingUp',
          bgColor: 'bg-success',
          trend: 'up',
          change: data?.conversionRate > 0 ? '' : 'No data'
        },
        {
          label: 'Avg. Payment Time',
          value: data?.avgPaymentTime ? `${data?.avgPaymentTime} days` : 'No data',
          icon: 'Clock',
          bgColor: 'bg-warning',
          trend: 'neutral',
          change: data?.avgPaymentTime ? '' : 'No payment data'
        }
      ];

      setSalesMetrics(metrics);
    } catch (error) {
      console.error('Error loading metrics:', error);
    }
  };

  // Load metrics on mount and when refresh is triggered
  useEffect(() => {
    loadMetrics();
  }, [organizationId, isAuthenticated, refreshTrigger]);

  // Enhanced real-time subscriptions for all modules with immediate updates
  useEffect(() => {
    if (!organizationId || !isAuthenticated) return;

    // Create universal callback that triggers immediate refresh for delete operations
    const handleDataChange = (payload) => {
      console.log(`Data changed in ${payload?.table}:`, payload?.eventType);

      // Handle delete operations with optimistic updates
      if (payload?.eventType === 'DELETE' && payload?.old?.id) {
        // Remove item immediately from current data
        setSalesData(prevData => prevData?.filter(item => item?.id !== payload?.old?.id));

        // Trigger a delayed refresh to ensure consistency
        triggerRefresh(500);
      } else {
        // For INSERT and UPDATE operations, trigger immediate refresh
        triggerRefresh(100);
      }
    };

    // Set up comprehensive subscriptions based on active tab
    const callbacks = {};

    switch (activeTab) {
      case 'customers':
        callbacks.customers = handleDataChange;
        break;
      case 'invoices':
        callbacks.invoices = handleDataChange;
        break;
      case 'quotes':
        callbacks.quotes = handleDataChange;
        break;
      case 'payment-links':
        callbacks.paymentLinks = handleDataChange;
        break;
      case 'payments-received':
        callbacks.payments = handleDataChange;
        break;
      case 'delivery-challans':
        callbacks.deliveryChallans = handleDataChange;
        break;
      case 'retainer-invoices':
        callbacks.retainerInvoices = handleDataChange;
        break;
      case 'recurring-invoices':
        callbacks.recurringInvoices = handleDataChange;
        break;
      case 'credit-notes':
        callbacks.creditNotes = handleDataChange;
        break;
      default:
        break;
    }

    const subscription = salesSubscriptions?.subscribeToAllChanges(organizationId, callbacks);

    return () => {
      subscription?.unsubscribeAll();
    };
  }, [activeTab, organizationId, isAuthenticated, triggerRefresh]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  // Customer options for filter and forms (derived from dedicated customer data)
  const customerOptions = React.useMemo(() => {
    return customerData?.map(customer => ({
      value: customer?.id,
      label: `${customer?.customer_name || customer?.email} ${customer?.company_name ? ` (${customer?.company_name})` : ''}`
    })) || [];
  }, [customerData]);

  // Status options based on schema enums
  const statusOptions = [
    // Customer statuses
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'suspended', label: 'Suspended' },
    { value: 'archived', label: 'Archived' },

    // Quote statuses
    { value: 'draft', label: 'Draft' },
    { value: 'sent', label: 'Sent' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'declined', label: 'Declined' },
    { value: 'expired', label: 'Expired' },
    { value: 'converted', label: 'Converted' },

    // Invoice statuses
    { value: 'paid', label: 'Paid' },
    { value: 'partially_paid', label: 'Partially Paid' },
    { value: 'overdue', label: 'Overdue' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'refunded', label: 'Refunded' },
    { value: 'viewed', label: 'Viewed' },

    // Credit Note statuses
    { value: 'issued', label: 'Issued' },
    { value: 'applied', label: 'Applied' },

    // Delivery statuses
    { value: 'pending', label: 'Pending' },
    { value: 'in_transit', label: 'In Transit' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'returned', label: 'Returned' },

    // Payment statuses
    { value: 'cleared', label: 'Cleared' },
    { value: 'bounced', label: 'Bounced' }
  ];

  // Calculate tab counts from actual data
  const getTabCounts = () => {
    // This would need separate API calls for accurate counts
    // For now, showing current data length
    return {
      customers: activeTab === 'customers' ? salesData?.length : 0,
      quotes: activeTab === 'quotes' ? salesData?.length : 0,
      retainerInvoices: activeTab === 'retainer-invoices' ? salesData?.length : 0,
      invoices: activeTab === 'invoices' ? salesData?.length : 0,
      recurringInvoices: activeTab === 'recurring-invoices' ? salesData?.length : 0,
      deliveryChallans: activeTab === 'delivery-challans' ? salesData?.length : 0,
      paymentLinks: activeTab === 'payment-links' ? salesData?.length : 0,
      paymentsReceived: activeTab === 'payments-received' ? salesData?.length : 0,
      creditNotes: activeTab === 'credit-notes' ? salesData?.length : 0
    };
  };

  const tabCounts = getTabCounts();

  // Enhanced filter data function
  const getFilteredData = () => {
    let filtered = [...salesData];

    // Apply text search
    if (filters?.customer) {
      filtered = filtered?.filter(item => {
        const searchText = filters?.customer?.toLowerCase();
        return item?.customer_name?.toLowerCase()?.includes(searchText) ||
          item?.email?.toLowerCase()?.includes(searchText) ||
          item?.customer?.customer_name?.toLowerCase()?.includes(searchText) ||
          item?.customer?.email?.toLowerCase()?.includes(searchText);
      });
    }

    // Apply status filter
    if (filters?.status) {
      filtered = filtered?.filter(item => item?.status === filters?.status);
    }

    // Apply date filters
    if (filters?.dateFrom) {
      filtered = filtered?.filter(item => {
        const itemDate = item?.created_at || item?.issue_date || item?.payment_date;
        return itemDate >= filters?.dateFrom;
      });
    }

    if (filters?.dateTo) {
      filtered = filtered?.filter(item => {
        const itemDate = item?.created_at || item?.issue_date || item?.payment_date;
        return itemDate <= filters?.dateTo;
      });
    }

    // Apply amount filters (for financial records)
    if (filters?.minAmount && (activeTab !== 'customers' && activeTab !== 'delivery-challans')) {
      filtered = filtered?.filter(item => {
        const amount = item?.total_amount || item?.amount || item?.retainer_amount || item?.amount_received || 0;
        return parseFloat(amount) >= parseFloat(filters?.minAmount);
      });
    }

    if (filters?.maxAmount && (activeTab !== 'customers' && activeTab !== 'delivery-challans')) {
      filtered = filtered?.filter(item => {
        const amount = item?.total_amount || item?.amount || item?.retainer_amount || item?.amount_received || 0;
        return parseFloat(amount) <= parseFloat(filters?.maxAmount);
      });
    }

    return filtered;
  };

  // Sort data
  const getSortedData = (data) => {
    return [...data]?.sort((a, b) => {
      let aValue = a?.[sortConfig?.key];
      let bValue = b?.[sortConfig?.key];

      // Handle nested properties (e.g., customer.customer_name)
      if (sortConfig?.key?.includes('.')) {
        const keys = sortConfig?.key?.split('.');
        aValue = keys?.reduce((obj, key) => obj?.[key], a);
        bValue = keys?.reduce((obj, key) => obj?.[key], b);
      }

      // Handle numeric values
      if (sortConfig?.key === 'total_amount' || sortConfig?.key === 'amount' || sortConfig?.key === 'retainer_amount') {
        aValue = parseFloat(aValue || 0);
        bValue = parseFloat(bValue || 0);
      }

      // Handle dates
      if (sortConfig?.key === 'created_at' || sortConfig?.key === 'issue_date' || sortConfig?.key === 'payment_date') {
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
      }

      if (aValue < bValue) {
        return sortConfig?.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig?.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      customer: '',
      dateFrom: '',
      dateTo: '',
      status: '',
      minAmount: '',
      maxAmount: ''
    });
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleCreateNew = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleActionDropdown = (actionType) => {
    console.log('Action selected:', actionType);

    // Handle different action types
    switch (actionType) {
      case 'add-customer': setActiveTab('customers');
        setEditingItem(null);
        setIsModalOpen(true);
        break;
      case 'create-quote': setActiveTab('quotes');
        setEditingItem(null);
        setIsModalOpen(true);
        break;
      case 'create-retainer': setActiveTab('retainer-invoices');
        setEditingItem(null);
        setIsModalOpen(true);
        break;
      case 'create-invoice': setActiveTab('invoices');
        setEditingItem(null);
        setIsModalOpen(true);
        break;
      case 'record-payment': setActiveTab('payments-received');
        setEditingItem(null);
        setIsModalOpen(true);
        break;
      case 'setup-recurring': setActiveTab('recurring-invoices');
        setEditingItem(null);
        setIsModalOpen(true);
        break;
      case 'create-challan': setActiveTab('delivery-challans');
        setEditingItem(null);
        setIsModalOpen(true);
        break;
      case 'create-credit-note': setActiveTab('credit-notes');
        setEditingItem(null);
        setIsModalOpen(true);
        break;
      default:
        console.log('Unknown action:', actionType);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  // Enhanced save handler with comprehensive module support
  const handleSave = async (data) => {
    if (!organizationId || !user?.id) {
      setError('Authentication required');
      return;
    }

    try {
      setLoading(true);
      let result;

      // Determine which service to call based on active tab
      switch (activeTab) {
        case 'customers':
          if (editingItem) {
            result = await customerService?.updateCustomer(editingItem?.id, data, organizationId);
          } else {
            result = await customerService?.createCustomer(data, organizationId, user?.id);
          }
          break;
        case 'invoices':
          if (editingItem) {
            result = await invoiceService?.updateInvoice(editingItem?.id, data, organizationId);
          } else {
            result = await invoiceService?.createInvoice(data, organizationId, user?.id);
          }
          break;
        case 'quotes':
          if (editingItem) {
            result = await quoteService?.updateQuote(editingItem?.id, data, organizationId);
          } else {
            result = await quoteService?.createQuote(data, organizationId, user?.id);
          }
          break;
        case 'payment-links':
          if (editingItem) {
            result = await paymentService?.updatePaymentLink(editingItem?.id, data, organizationId);
          } else {
            result = await paymentService?.createPaymentLink(data, organizationId, user?.id);
          }
          break;
        case 'payments-received':
          if (editingItem) {
            result = await paymentService?.updatePaymentReceived(editingItem?.id, data, organizationId);
          } else {
            result = await paymentService?.createPaymentReceived(data, organizationId, user?.id);
          }
          break;
        case 'delivery-challans':
          if (editingItem) {
            result = await deliveryService?.updateDeliveryChallan(editingItem?.id, data, organizationId);
          } else {
            result = await deliveryService?.createDeliveryChallan(data, organizationId, user?.id);
          }
          break;
        case 'retainer-invoices':
          if (editingItem) {
            result = await retainerService?.updateRetainerInvoice(editingItem?.id, data, organizationId);
          } else {
            result = await retainerService?.createRetainerInvoice(data, organizationId, user?.id);
          }
          break;
        case 'recurring-invoices':
          if (editingItem) {
            result = await recurringService?.updateRecurringInvoice(editingItem?.id, data, organizationId);
          } else {
            result = await recurringService?.createRecurringInvoice(data, organizationId, user?.id);
          }
          break;
        case 'credit-notes':
          if (editingItem) {
            result = await creditNoteService?.updateCreditNote(editingItem?.id, data, organizationId);
          } else {
            result = await creditNoteService?.createCreditNote(data, organizationId, user?.id);
          }
          break;
        default:
          console.log('Save operation not implemented for:', activeTab);
          setError(`Save operation not available for ${activeTab}`);
          return;
      }

      if (result?.error) {
        setError(`Failed to save: ${result?.error?.message || result?.error}`);
      } else {
        // Close modal first
        setIsModalOpen(false);
        setEditingItem(null);
        setError(null);

        // Force immediate refresh to show new data
        forceRefresh();

        // Reload customer options if a customer was created/updated
        if (activeTab === 'customers') {
          loadCustomerOptions();
        }

        // Show success feedback
        console.log(`${activeTab?.slice(0, -1)} ${editingItem ? 'updated' : 'created'} successfully`);
      }
    } catch (error) {
      console.error('Error saving:', error);
      setError('Failed to save. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle view item details with better error handling for deleted items
  const handleViewItem = async (item) => {
    if (!organizationId || !item?.id) {
      setError('Invalid item or organization context');
      return;
    }

    setViewingItem(item);
    setViewLoading(true);
    setIsViewModalOpen(true);

    try {
      let result;

      // Fetch detailed information based on active tab
      switch (activeTab) {
        case 'customers':
          result = await customerService?.getCustomerDetails(item?.id, organizationId);
          break;
        case 'invoices':
          result = await invoiceService?.getInvoiceDetails(item?.id, organizationId);
          break;
        case 'quotes':
          result = await quoteService?.getQuoteDetails(item?.id, organizationId);
          break;
        case 'payment-links':
          result = await paymentService?.getPaymentLinkDetails(item?.id, organizationId);
          break;
        case 'payments-received':
          result = await paymentService?.getPaymentReceivedDetails(item?.id, organizationId);
          break;
        case 'delivery-challans':
          result = await deliveryService?.getDeliveryChallanDetails(item?.id, organizationId);
          break;
        case 'retainer-invoices':
          result = await retainerService?.getRetainerInvoiceDetails(item?.id, organizationId);
          break;
        case 'recurring-invoices':
          result = await recurringService?.getRecurringInvoiceDetails(item?.id, organizationId);
          break;
        case 'credit-notes':
          result = await creditNoteService?.getCreditNoteDetails(item?.id, organizationId);
          break;
        default:
          result = { data: item, error: null }; // Use existing item data as fallback
          break;
      }

      if (result?.error) {
        // Handle specific error for deleted items
        if (result?.error?.code === 'PGRST116' || result?.error?.message?.includes('0 rows')) {
          setError(`This ${activeTab?.slice(0, -1)} may have been deleted. Refreshing data...`);
          // Close modal and refresh data
          handleCloseViewModal();
          forceRefresh();
          return;
        }
        setError(`Failed to load details: ${result?.error?.message}`);
      } else {
        setViewingItem(result?.data || item);
      }
    } catch (error) {
      console.error('Error fetching item details:', error);
      // Handle network or other errors
      if (error?.message?.includes('0 rows') || error?.code === 'PGRST116') {
        setError(`This ${activeTab?.slice(0, -1)} may have been deleted. Refreshing data...`);
        handleCloseViewModal();
        forceRefresh();
      } else {
        setError('Failed to load item details. Please try again.');
      }
    } finally {
      setViewLoading(false);
    }
  };

  // Handle delete item
  const handleDeleteItem = (item) => {
    setDeletingItem(item);
    setDeleteError(null);
    setIsDeleteModalOpen(true);
  };

  // Handle duplicate item - creates duplicate directly in the list
  const handleDuplicate = async (item) => {
    // For customers, open the duplicate modal to allow email editing
    if (activeTab === 'customers') {
      setDuplicatingCustomer(item);
      setIsDuplicateCustomerModalOpen(true);
      return;
    }

    // For other item types, proceed with direct duplication
    if (!organizationId || !user?.id) {
      setError('Authentication required');
      return;
    }

    try {
      setLoading(true);

      // Prepare duplicate data by removing unique identifiers
      const prepareDuplicateData = (originalItem) => {
        const duplicateData = { ...originalItem };

        // Remove unique identifiers
        delete duplicateData?.id;
        delete duplicateData?.created_at;
        delete duplicateData?.updated_at;

        // Remove reference numbers (will be auto-generated)
        delete duplicateData?.invoice_number;
        delete duplicateData?.quote_number;
        delete duplicateData?.customer_number;
        delete duplicateData?.payment_reference;
        delete duplicateData?.challan_number;
        delete duplicateData?.retainer_number;
        delete duplicateData?.recurring_reference;
        delete duplicateData?.credit_note_number;
        delete duplicateData?.link_reference;

        // Reset payment/amount fields for invoices
        if (duplicateData?.amount_paid) {
          duplicateData.amount_paid = 0;
        }
        if (duplicateData?.paid_at) {
          delete duplicateData?.paid_at;
        }

        // Set status appropriately based on item type
        // Customers should be 'active', not 'draft' (draft is only for invoices/quotes)
        if (activeTab === 'customers') {
          duplicateData.status = 'active'; // Customers don't have 'draft' status
        } else if (duplicateData?.status) {
          duplicateData.status = 'draft'; // For invoices, quotes, etc.
        }

        // Remove nested objects that shouldn't be copied directly
        if (duplicateData?.customer && typeof duplicateData?.customer === 'object') {
          duplicateData.customer_id = duplicateData?.customer?.id || duplicateData?.customer_id;
          delete duplicateData?.customer;
        }
        if (duplicateData?.reference_invoice && typeof duplicateData?.reference_invoice === 'object') {
          delete duplicateData?.reference_invoice;
        }

        // Handle line items for invoices and quotes
        if (activeTab === 'invoices' && duplicateData?.invoice_line_items) {
          // Convert invoice_line_items to line_items format expected by create service
          duplicateData.line_items = duplicateData?.invoice_line_items?.map(item => ({
            item_id: item?.item_id,
            item_description: item?.item_description,
            quantity: item?.quantity,
            unit_price: item?.unit_price,
            tax_rate: item?.tax_rate,
            tax_amount: item?.tax_amount,
            notes: item?.notes,
            weight_per_unit: item?.weight_per_unit,
            account_id: item?.account_id || item?.account?.id
          }));
          delete duplicateData?.invoice_line_items;
        } else if (activeTab === 'quotes' && duplicateData?.quote_line_items) {
          // Convert quote_line_items to line_items format expected by create service
          duplicateData.line_items = duplicateData?.quote_line_items?.map(item => ({
            item_id: item?.item_id,
            item_description: item?.item_description,
            quantity: item?.quantity,
            unit_price: item?.unit_price,
            tax_rate: item?.tax_rate,
            tax_amount: item?.tax_amount,
            notes: item?.notes,
            weight_per_unit: item?.weight_per_unit,
            account_id: item?.account_id || item?.account?.id
          }));
          delete duplicateData?.quote_line_items;
        }

        return duplicateData;
      };

      const duplicateData = prepareDuplicateData(item);
      let result;

      // Call appropriate create service based on active tab
      switch (activeTab) {
        case 'invoices':
          result = await invoiceService?.createInvoice(duplicateData, organizationId, user?.id);
          break;
        case 'quotes':
          result = await quoteService?.createQuote(duplicateData, organizationId, user?.id);
          break;
        case 'payment-links':
          result = await paymentService?.createPaymentLink(duplicateData, organizationId, user?.id);
          break;
        case 'payments-received':
          result = await paymentService?.createPaymentReceived(duplicateData, organizationId, user?.id);
          break;
        case 'delivery-challans':
          result = await deliveryService?.createDeliveryChallan(duplicateData, organizationId, user?.id);
          break;
        case 'retainer-invoices':
          result = await retainerService?.createRetainerInvoice(duplicateData, organizationId, user?.id);
          break;
        case 'recurring-invoices':
          result = await recurringService?.createRecurringInvoice(duplicateData, organizationId, user?.id);
          break;
        case 'credit-notes':
          result = await creditNoteService?.createCreditNote(duplicateData, organizationId, user?.id);
          break;
        default:
          setError(`Duplicate operation not supported for ${activeTab}`);
          return;
      }

      if (result?.error) {
        setError(`Failed to duplicate item: ${result?.error?.message || result?.error}`);
      } else {
        // Optimistically add the new item to the list
        if (result?.data) {
          setSalesData(prevData => [result?.data, ...prevData]);
        }

        setError(null);

        // Force refresh to ensure consistency
        forceRefresh();

        // Show success feedback
        console.log(`${activeTab?.slice(0, -1)} duplicated successfully`);
      }
    } catch (error) {
      console.error('Error duplicating item:', error);
      setError('Failed to duplicate item. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle duplicate customer with new email
  const handleDuplicateCustomerConfirm = async (newEmail) => {
    if (!organizationId || !user?.id || !duplicatingCustomer) {
      setError('Authentication required');
      setIsDuplicateCustomerModalOpen(false);
      return;
    }

    try {
      setLoading(true);

      // Prepare duplicate data by removing unique identifiers
      const duplicateData = { ...duplicatingCustomer };

      // Remove unique identifiers
      delete duplicateData?.id;
      delete duplicateData?.created_at;
      delete duplicateData?.updated_at;
      delete duplicateData?.customer_number;

      // Update email with the new one
      duplicateData.email = newEmail;
      duplicateData.status = 'active';

      // Remove nested objects that shouldn't be copied directly
      if (duplicateData?.customer && typeof duplicateData?.customer === 'object') {
        delete duplicateData?.customer;
      }

      // Create the duplicate customer
      let result = await customerService?.createCustomer(duplicateData, organizationId, user?.id);

      if (result?.error) {
        setError(`Failed to duplicate customer: ${result?.error?.message || result?.error}`);
      } else {
        // Optimistically add the new item to the list
        if (result?.data) {
          setSalesData(prevData => [result?.data, ...prevData]);
        }

        setError(null);

        // Force refresh to ensure consistency
        forceRefresh();

        // Reload customer options
        loadCustomerOptions();

        // Close modal and reset state
        setIsDuplicateCustomerModalOpen(false);
        setDuplicatingCustomer(null);

        // Show success feedback
        console.log('Customer duplicated successfully');
      }
    } catch (error) {
      console.error('Error duplicating customer:', error);
      setError('Failed to duplicate customer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Close duplicate customer modal
  const handleCloseDuplicateCustomerModal = () => {
    setIsDuplicateCustomerModalOpen(false);
    setDuplicatingCustomer(null);
  };

  // Handle send item (for invoices, quotes, etc.)
  const handleSendItem = (item) => {
    // Placeholder for send functionality - could open email modal, mark as sent, etc.
    console.log('Send item:', item);
    // You could implement actual send functionality here
    setError('Send functionality not yet implemented');
  };

  // Handle record payment for invoices
  const handleRecordPayment = (invoice) => {
    setPaymentInvoice(invoice);
    setIsRecordPaymentModalOpen(true);
  };

  // Close record payment modal
  const handleCloseRecordPaymentModal = () => {
    setIsRecordPaymentModalOpen(false);
    setPaymentInvoice(null);
    setRecordPaymentLoading(false);
  };

  // Submit payment recording
  const handleSubmitPayment = async (paymentData) => {
    if (!organizationId || !user?.id) {
      setError('Authentication required');
      return;
    }

    setRecordPaymentLoading(true);
    try {
      // Create payment received record
      let result = await paymentService?.createPaymentReceived(paymentData, organizationId, user?.id);

      if (result?.error) {
        setError(`Failed to record payment: ${result?.error?.message || result?.error}`);
      } else {
        // Update the invoice amount_paid and status
        const totalAmount = parseFloat(paymentInvoice?.total_amount || 0);
        const currentAmountPaid = parseFloat(paymentInvoice?.amount_paid || 0);
        const newAmountPaid = currentAmountPaid + parseFloat(paymentData?.amount_received);
        const newBalance = totalAmount - newAmountPaid;

        // Determine new invoice status
        let newStatus = paymentInvoice?.status;
        if (newBalance <= 0) {
          newStatus = 'paid';
        } else if (newAmountPaid > 0) {
          newStatus = 'partially_paid';
        }

        // Update invoice with new payment information
        const invoiceUpdate = await invoiceService?.updateInvoice(
          paymentInvoice?.id,
          {
            amount_paid: newAmountPaid,
            status: newStatus,
            paid_at: newBalance <= 0 ? new Date()?.toISOString() : paymentInvoice?.paid_at
          },
          organizationId
        );

        if (invoiceUpdate?.error) {
          console.error('Error updating invoice status:', invoiceUpdate?.error);
        }

        // Close modal first
        handleCloseRecordPaymentModal();
        setError(null);

        // Force immediate refresh to show updated data
        forceRefresh();

        // Show success feedback
        console.log('Payment recorded successfully');
      }
    } catch (error) {
      console.error('Error recording payment:', error);
      setError('Failed to record payment. Please try again.');
    } finally {
      setRecordPaymentLoading(false);
    }
  };

  // Confirm delete operation with optimistic updates
  const handleConfirmDelete = async (forceDelete = false) => {
    if (!deletingItem || !organizationId) {
      return;
    }

    setDeleteLoading(true);
    setDeleteError(null);

    try {
      let result;

      // Call appropriate delete service based on active tab
      switch (activeTab) {
        case 'customers':
          result = forceDelete
            ? await customerService?.forceDeleteCustomer(deletingItem?.id, organizationId)
            : await customerService?.deleteCustomer(deletingItem?.id, organizationId);
          break;
        case 'invoices':
          result = forceDelete
            ? await invoiceService?.forceDeleteInvoice(deletingItem?.id, organizationId)
            : await invoiceService?.deleteInvoice(deletingItem?.id, organizationId);
          break;
        case 'quotes':
          result = forceDelete
            ? await quoteService?.forceDeleteQuote(deletingItem?.id, organizationId)
            : await quoteService?.deleteQuote(deletingItem?.id, organizationId);
          break;
        case 'payment-links':
          result = await paymentService?.deletePaymentLink(deletingItem?.id, organizationId);
          break;
        case 'payments-received':
          result = await paymentService?.deletePaymentReceived(deletingItem?.id, organizationId);
          break;
        case 'delivery-challans':
          result = await deliveryService?.deleteDeliveryChallan(deletingItem?.id, organizationId);
          break;
        case 'retainer-invoices':
          result = forceDelete
            ? await retainerService?.forceDeleteRetainerInvoice(deletingItem?.id, organizationId)
            : await retainerService?.deleteRetainerInvoice(deletingItem?.id, organizationId);
          break;
        case 'recurring-invoices':
          result = forceDelete
            ? await recurringService?.forceDeleteRecurringInvoice(deletingItem?.id, organizationId)
            : await recurringService?.deleteRecurringInvoice(deletingItem?.id, organizationId);
          break;
        case 'credit-notes':
          result = await creditNoteService?.deleteCreditNote(deletingItem?.id, organizationId);
          break;
        default:
          setDeleteError('Delete operation not supported for this type');
          return;
      }

      if (result?.error) {
        if (result?.error?.type === 'constraint_violation') {
          setDeleteError(result?.error);
        } else {
          setDeleteError({
            type: 'deletion_error',
            message: `Failed to delete: ${result?.error?.message || result?.error}`,
            constraints: []
          });
        }
      } else {
        // Success - perform optimistic update first for immediate UI response
        setSalesData(prevData => prevData?.filter(item => item?.id !== deletingItem?.id));

        // Close modal immediately
        setIsDeleteModalOpen(false);
        setDeletingItem(null);
        setDeleteError(null);

        // Force immediate refresh to sync with database
        forceRefresh();

        // Reload customer options if a customer was deleted
        if (activeTab === 'customers') {
          loadCustomerOptions();
        }

        // Show success message
        console.log(`${activeTab?.slice(0, -1)} deleted successfully`);
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      setDeleteError({
        type: 'deletion_error',
        message: 'Failed to delete item. Please try again.',
        constraints: []
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  // Close delete modal
  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeletingItem(null);
    setDeleteError(null);
    setDeleteLoading(false);
  };

  // Close view modal
  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setViewingItem(null);
    setViewLoading(false);
  };

  // Enhanced handleAction with record payment functionality
  const handleAction = async (action, item) => {
    try {
      switch (action) {
        case 'view':
          await handleViewItem(item);
          break;
        case 'edit':
          handleEdit(item);
          break;
        case 'duplicate':
          await handleDuplicate(item);
          break;
        case 'send':
          handleSendItem(item);
          break;
        case 'record-payment':
          handleRecordPayment(item);
          break;
        case 'delete':
          handleDeleteItem(item);
          break;
        case 'convert':
          // CRITICAL FIX: Check activeTab instead of item.type since quotes don't have a type field
          if (activeTab === 'quotes' && organizationId && user?.id) {
            // Convert quote to invoice
            const invoiceData = {
              customer_id: item?.customer_id,
              subtotal: item?.subtotal,
              tax_amount: item?.tax_amount,
              total_amount: item?.total_amount,
              due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0], // 30 days from now
              notes: `Converted from quote ${item?.quote_number}`,
              status: 'draft'
            };

            let result = await invoiceService?.convertQuoteToInvoice(item?.id, invoiceData, organizationId, user?.id);
            if (result?.error) {
              setError(`Failed to convert quote: ${result?.error?.message}`);
            } else {
              // Force immediate refresh to show new data
              forceRefresh();

              // Show success feedback
              console.log('Quote converted to invoice successfully');
              setError(null);
            }
          } else {
            console.log('Convert action not applicable for current context:', { activeTab, item });
          }
          break;
        case 'create':
          handleCreateNew();
          break;
        default:
          console.log('Unknown action:', action, item);
      }
    } catch (error) {
      console.error('Error handling action:', error);
      setError('Failed to perform action. Please try again.');
    }
  };

  // Show loading state
  if (loading && salesData?.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex pt-16">
          <Sidebar
            isCollapsed={isSidebarCollapsed}
            onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          />
          <main className="flex-1 p-6 overflow-auto">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading sales data...</p>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const filteredData = getFilteredData();
  const sortedData = getSortedData(filteredData);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="flex pt-16">
        <Sidebar
          isCollapsed={isSidebarCollapsed}
          onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />

        <main className="flex-1 p-6 overflow-auto">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Page Header with Refresh Indicator */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div>
                  <h1 className="text-3xl font-bold text-foreground">Sales Hub</h1>
                  <p className="text-muted-foreground mt-1">
                    Comprehensive sales management including customers, quotes, invoices, payments, and delivery tracking
                  </p>
                </div>
                {(isRefreshing || loading) && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                    <span>{isRefreshing ? 'Refreshing...' : 'Loading...'}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  iconName="RefreshCw"
                  iconPosition="left"
                  onClick={() => forceRefresh()}
                  disabled={isRefreshing || loading}
                >
                  Refresh
                </Button>
                <Button
                  variant="outline"
                  iconName="Download"
                  iconPosition="left"
                >
                  Export Data
                </Button>
                <SalesActionDropdown onAction={handleActionDropdown} />
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                  <div className="ml-auto pl-3">
                    <button
                      type="button"
                      className="text-red-400 hover:text-red-600"
                      onClick={() => setError(null)}
                    >
                      <span className="sr-only">Dismiss</span>
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Sales Metrics */}
            <SalesMetricsPanel metrics={salesMetrics} />

            {/* AI Insights */}
            <AIInsightsPanel
              organizationId={organizationId}
              refreshTrigger={refreshTrigger}
            />

            {/* Tab Navigation */}
            <TabNavigation
              activeTab={activeTab}
              onTabChange={setActiveTab}
              tabCounts={tabCounts}
            />

            {/* Filter Toolbar */}
            <SalesFilterToolbar
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              onCreateNew={handleCreateNew}
              customerOptions={customerOptions}
              statusOptions={statusOptions}
              activeTab={activeTab}
            />

            {/* Data Table */}
            <SalesDataTable
              data={getSortedData(getFilteredData())}
              onAction={handleAction}
              sortConfig={sortConfig}
              onSort={handleSort}
              activeTab={activeTab}
              loading={loading}
            />

            {/* Show empty state when no data */}
            {!loading && sortedData?.length === 0 && (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2h4a1 1 0 011 1v1a1 1 0 01-1 1h-1v9a2 2 0 01-2 2H6a2 2 0 01-2-2V7H3a1 1 0 01-1-1V5a1 1 0 011-1h4zM9 4h6V3H9v1zm2 8a1 1 0 112 0v4a1 1 0 11-2 0v-4zm4-1a1 1 0 10-2 0v4a1 1 0 102 0v-4z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No {activeTab} found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {filters?.customer || filters?.status || filters?.dateFrom || filters?.dateTo
                    ? 'Try adjusting your filters to see more results.'
                    : `Get started by creating your first ${activeTab?.slice(0, -1) || 'item'}.`
                  }
                </p>
                {(!filters?.customer && !filters?.status && !filters?.dateFrom && !filters?.dateTo) && (
                  <div className="mt-6">
                    <Button onClick={handleCreateNew}>
                      Create {activeTab?.slice(0, -1) || 'New Item'}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Sales Form Modal */}
      <SalesFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingItem(null);
          setError(null);
        }}
        type={activeTab}
        item={editingItem}
        onSave={handleSave}
        customerOptions={customerOptions}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        item={deletingItem}
        type={activeTab}
        constraintError={deleteError}
        loading={deleteLoading}
      />

      {/* View Details Modal */}
      <ViewDetailsModal
        isOpen={isViewModalOpen}
        onClose={handleCloseViewModal}
        item={viewingItem}
        type={activeTab}
        loading={viewLoading}
      />

      {/* Record Payment Modal */}
      <RecordPaymentModal
        isOpen={isRecordPaymentModalOpen}
        onClose={handleCloseRecordPaymentModal}
        invoice={paymentInvoice}
        customerOptions={customerOptions}
        onRecordPayment={handleSubmitPayment}
      />

      {/* Duplicate Customer Modal */}
      <DuplicateCustomerModal
        isOpen={isDuplicateCustomerModalOpen}
        onClose={handleCloseDuplicateCustomerModal}
        onConfirm={handleDuplicateCustomerConfirm}
        customer={duplicatingCustomer}
        isLoading={loading}
      />
    </div>
  );
};

export default SalesHub;
